import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import axios from 'axios';
import {
  ChatHistoryState,
  ChatHistoryContextType,
  ChatHistoryItem,
  ChatHistoryFilters,
  ChatHistoryResponse,
  ChatStatistics,
  CHAT_HISTORY_LIMITS,
} from '../types/chat';

type ChatHistoryAction =
  | { type: 'LOADING_START' }
  | { type: 'LOADING_MORE_START' }
  | { type: 'SESSIONS_SUCCESS'; payload: { sessions: ChatHistoryItem[]; total: number; reset?: boolean } }
  | { type: 'STATISTICS_SUCCESS'; payload: ChatStatistics }
  | { type: 'DELETE_SESSION'; payload: number }
  | { type: 'UPDATE_SESSION'; payload: { id: number; updates: Partial<ChatHistoryItem> } }
  | { type: 'UPDATE_FILTERS'; payload: Partial<ChatHistoryFilters> }
  | { type: 'ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOADING_END' }
  | { type: 'LOADING_MORE_END' };

const initialState: ChatHistoryState = {
  sessions: [],
  statistics: null,
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  error: null,
  filters: {
    limit: CHAT_HISTORY_LIMITS.INITIAL_LOAD,
    offset: 0,
  },
};

const chatHistoryReducer = (state: ChatHistoryState, action: ChatHistoryAction): ChatHistoryState => {
  switch (action.type) {
    case 'LOADING_START':
      return { ...state, isLoading: true, error: null };
    
    case 'LOADING_MORE_START':
      return { ...state, isLoadingMore: true, error: null };
    
    case 'SESSIONS_SUCCESS':
      const { sessions, total, reset } = action.payload;
      return {
        ...state,
        sessions: reset ? sessions : [...state.sessions, ...sessions],
        hasMore: reset 
          ? sessions.length < total && sessions.length === CHAT_HISTORY_LIMITS.INITIAL_LOAD
          : state.sessions.length + sessions.length < total,
        isLoading: false,
        isLoadingMore: false,
        error: null,
      };
    
    case 'STATISTICS_SUCCESS':
      return { ...state, statistics: action.payload };
    
    case 'DELETE_SESSION':
      return {
        ...state,
        sessions: state.sessions.filter(session => session.id !== action.payload),
      };
    
    case 'UPDATE_SESSION':
      return {
        ...state,
        sessions: state.sessions.map(session =>
          session.id === action.payload.id
            ? { ...session, ...action.payload.updates }
            : session
        ),
      };
    
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };
    
    case 'ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isLoadingMore: false,
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'LOADING_END':
      return { ...state, isLoading: false };
    
    case 'LOADING_MORE_END':
      return { ...state, isLoadingMore: false };
    
    default:
      return state;
  }
};

const ChatHistoryContext = createContext<ChatHistoryContextType | undefined>(undefined);

export const useChatHistory = (): ChatHistoryContextType => {
  const context = useContext(ChatHistoryContext);
  if (context === undefined) {
    throw new Error('useChatHistory must be used within a ChatHistoryProvider');
  }
  return context;
};

interface ChatHistoryProviderProps {
  children: ReactNode;
}

export const ChatHistoryProvider = ({ children }: ChatHistoryProviderProps) => {
  const [state, dispatch] = useReducer(chatHistoryReducer, initialState);

  const loadChatHistory = useCallback(async (reset: boolean = false): Promise<void> => {
    dispatch({ type: reset ? 'LOADING_START' : 'LOADING_MORE_START' });
    
    try {
      const params = {
        ...state.filters,
        offset: reset ? 0 : state.sessions.length,
        limit: reset ? CHAT_HISTORY_LIMITS.INITIAL_LOAD : CHAT_HISTORY_LIMITS.LOAD_MORE,
      };

      const response = await axios.get<{
        success: boolean;
        data: {
          sessions: ChatHistoryItem[];
          pagination: {
            total: number;
            limit: number;
            offset: number;
            hasMore: boolean;
          };
        };
      }>('/api/chat/sessions', { params });
      
      dispatch({
        type: 'SESSIONS_SUCCESS',
        payload: {
          sessions: response.data.data.sessions,
          total: response.data.data.pagination.total,
          reset,
        },
      });
    } catch (error: any) {
      let errorMessage = 'チャット履歴の取得に失敗しました';
      
      if (error.response?.status === 401) {
        errorMessage = 'ログインが必要です';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'ERROR', payload: errorMessage });
    }
  }, [state.filters, state.sessions.length]);

  const loadMoreSessions = useCallback(async (): Promise<void> => {
    if (!state.hasMore || state.isLoadingMore) return;
    
    await loadChatHistory(false);
  }, [loadChatHistory, state.hasMore, state.isLoadingMore]);

  const updateFilters = useCallback((filters: Partial<ChatHistoryFilters>): void => {
    dispatch({ type: 'UPDATE_FILTERS', payload: filters });
  }, []);

  const deleteSession = useCallback(async (sessionId: number): Promise<void> => {
    try {
      await axios.delete(`/api/chat/sessions/${sessionId}`);
      dispatch({ type: 'DELETE_SESSION', payload: sessionId });
    } catch (error: any) {
      let errorMessage = 'チャットの削除に失敗しました';
      
      if (error.response?.status === 401) {
        errorMessage = 'ログインが必要です';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  const archiveSession = useCallback(async (sessionId: number): Promise<void> => {
    try {
      await axios.patch(`/api/chat/sessions/${sessionId}/archive`);
      dispatch({ type: 'DELETE_SESSION', payload: sessionId });
    } catch (error: any) {
      let errorMessage = 'チャットのアーカイブに失敗しました';
      
      if (error.response?.status === 401) {
        errorMessage = 'ログインが必要です';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  const updateSession = useCallback(async (sessionId: number, updates: { title?: string; isPinned?: boolean }): Promise<void> => {
    try {
      // まずローカル状態を楽観的に更新
      dispatch({ 
        type: 'UPDATE_SESSION', 
        payload: { id: sessionId, updates } 
      });

      // APIに送信するデータを準備
      const updateData: any = {};
      if (updates.title !== undefined) {
        updateData.title = updates.title;
      }
      if (updates.isPinned !== undefined) {
        updateData.isPinned = updates.isPinned;
      }

      // API呼び出し
      await axios.put(`/api/chat/sessions/${sessionId}`, updateData);
      
    } catch (error: any) {
      // エラーが発生した場合、チャット履歴を再読み込みして状態を元に戻す
      await loadChatHistory(true);
      
      let errorMessage = 'チャットの更新に失敗しました';
      
      if (error.response?.status === 401) {
        errorMessage = 'ログインが必要です';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'ERROR', payload: errorMessage });
      throw error;
    }
  }, [loadChatHistory]);

  const refreshStatistics = useCallback(async (): Promise<void> => {
    try {
      // TODO: 統計情報のAPIエンドポイントが実装されたら有効にする
      // const response = await axios.get<ChatStatistics>('/api/chat/statistics');
      // dispatch({ type: 'STATISTICS_SUCCESS', payload: response.data });
    } catch (error: any) {
      // Statistics error is not critical, just log it
      console.warn('Failed to load chat statistics:', error);
    }
  }, []);

  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // リアルタイム更新用：新しいセッションが作成されたときやメッセージが送信されたときに履歴を更新
  const refreshChatHistory = useCallback(async (): Promise<void> => {
    await loadChatHistory(true);
  }, [loadChatHistory]);

  const value: ChatHistoryContextType = {
    ...state,
    loadChatHistory,
    loadMoreSessions,
    updateFilters,
    deleteSession,
    archiveSession,
    updateSession,
    clearError,
    refreshStatistics,
    refreshChatHistory,
  };

  return <ChatHistoryContext.Provider value={value}>{children}</ChatHistoryContext.Provider>;
};